import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, Inject, Optional } from '@angular/core';
import { asFormControl } from '@feature/forms/form-model';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  FundDDQRunService,
  InitializeEmittentDefaultTemplatesPayload,
  IssuerSearchResult,
  FundSearchResult,
  IssuerAdminSearchResult,
} from '@core/fund-ddq-run/fund-ddq-run.service';
import { FormErrorService } from '@core/errors/form-error.service';
import {
  CreateFundDDQRunRequest,
  FundDDQRun,
  CreateFundDialogData,
  LPInviteRequest,
  GPInviteRequest,
} from '@core/models/fund-ddq-run.model';
import {
  CreateFundDialogFormService,
  FlowType,
  DIRECT_DEAL_ASSET_CLASS,
  isInviteFlow,
  isCreateFlow,
  isDirectDealFlow,
} from './create-fund-dialog-form.service';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';
import { OrganizationQuery } from '@core/organization/organization.query';
import { FormOption } from '@feature/forms/form-controls/form-option';
import { ProductQuery } from '@core/product/product.query';
import { productRoute, dataroomRoute } from '@shared/shared-routes';
import { OrganizationContextService } from '@core/context/organization-context.service';
import { ValueObject } from '@core/models/value-object.model';
import { ClientService } from '@core/client/client.service';
import { AppStoreQuery } from '@core/state';
import { getSubscriptionDenialReason, shouldShowDdOptionUpsell } from '@core/subscription/gp-ddq.constants';
import { SubscriptionUpgradeDialogComponent } from '@shared/subscription/subscription-upgrade-dialog/subscription-upgrade-dialog.component';
import {
  buildFundDdqDataroomRoute,
  fundDdqNavigationExtras,
  extractClientContextFromUrl,
  resolveClientCompanyForKeepPrivate,
} from '../fund-ddq-product-navigation.util';
import { DdqInvitationNavService } from '@core/ddq-invitation/ddq-invitation-nav.service';
import { take, map, debounceTime, distinctUntilChanged, startWith, catchError, switchMap } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, Observable, of, combineLatest } from 'rxjs';
import { FormControl } from '@angular/forms';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'wai-create-fund-dialog',
  templateUrl: './create-fund-dialog.component.html',
  styleUrls: ['./create-fund-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateFundDialogComponent implements OnInit {
  public form = this.formService.form;
  public asFormControl = asFormControl;
  public issuerOptions$: Observable<IssuerSearchResult[]> = of([]);
  public fundOptions$: Observable<FundSearchResult[]> = of([]);
  public issuerAdminResult: IssuerAdminSearchResult | null = null;
  public readonly inviteNewAdminChoiceValue = 'invite_new_admin';
  public readonly issuerAdminEmailDropdownPlaceholderLabel = 'select Existing or Invite New';
  public issuerAdminEmailChoiceOptions: FormOption[] = [
    new FormOption('Select Existing or Invite New', ''),
    new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
  ];

  private issuerAdminsList: IssuerAdminSearchResult[] = [];
  private readonly issuerAdminsListSubject = new BehaviorSubject<IssuerAdminSearchResult[]>([]);
  public issuerAdminEmailOptions$: Observable<IssuerAdminSearchResult[]> = of([]);
  public isPreparingInviteAdminFund = false;
  public isLoadingIssuerAdmin = false;
  public showFundTypeFields = false; // Show fundType and fundSubtype when no products found
  public isCreating = false; // Loading state for create fund action
  public isNewInvestor = false; // Flag to show new investor fields when exists === false
  public hasSearchedIssuerAdmin = false; // Flag to track if search has been performed
  public showInvestorInviteWarning = false; // Shown when searching LP/issuer admins returns empty
  public assetClasses$ = this.formService.assetClasses;
  public assetSubclasses$ = this.formService.assetSubclasses;
  public salutations$ = this.formService.salutations;
  public isEmittentOrganization$: Observable<boolean> = this.organizationQuery
    .selectChosenOrganizationType()
    .pipe(map((orgType) => orgType === 'emittent'));

  public isInvestorOrganization$: Observable<boolean> = this.organizationQuery
    .selectChosenOrganizationType()
    .pipe(map((orgType) => orgType === 'investor' || orgType === 'client'));

  /**
   * Dialog title key resolved synchronously so the h1 always has a concrete i18n key
   * (avoids empty title while async org-type pipes have not emitted).
   */
  public get dialogTitleKey(): string {
    const orgType = this.organizationQuery.getActive()?.type as string | undefined;
    if (orgType === 'emittent') {
      return 'INTL_INVITE_LP';
    }
    if (this.data?.skipToInviteStep) {
      return 'INTL_INVITE_GP';
    }
    return 'INTL_CREATE_FUND_OR_INVITE';
  }

  public isInviteFlow = isInviteFlow;
  public isCreateFlow = isCreateFlow;
  public isDirectDealFlow = isDirectDealFlow;

  public isDirectDealAssetClass(): boolean {
    return this.form.get('fundType')?.value === DIRECT_DEAL_ASSET_CLASS;
  }

  public onFlowTypeSelected(flowType: FlowType): void {
    const previousFlow = this.form.get('flowType')?.value as FlowType | null;
    const wasDirectDeal = isDirectDealFlow(previousFlow);
    const willBeDirectDeal = isDirectDealFlow(flowType);

    // Emit so fund-name search re-runs with/without asset_class=direct-deal
    this.form.get('flowType')?.setValue(flowType);

    if (willBeDirectDeal) {
      this.form.patchValue(
        {
          fundType: DIRECT_DEAL_ASSET_CLASS,
          fundSubtype: null,
          keepPrivate: false,
          clientCompany: null,
        },
        { emitEvent: false },
      );
      this.formService.selectAssetClass(DIRECT_DEAL_ASSET_CLASS);
      const fundSubtypeControl = this.form.get('fundSubtype');
      if (fundSubtypeControl) {
        setTimeout(() => fundSubtypeControl.enable(), 100);
      }
    } else if (wasDirectDeal && this.form.get('fundType')?.value === DIRECT_DEAL_ASSET_CLASS) {
      this.form.patchValue(
        {
          fundType: null,
          fundSubtype: null,
          keepPrivate: false,
          clientCompany: null,
        },
        { emitEvent: false },
      );
      this.formService.selectAssetClass(null);
      this.form.get('fundSubtype')?.disable();
    }

    // Clear a previously selected product when switching fund vs Direct Deal scope
    if (wasDirectDeal !== willBeDirectDeal && this.form.get('productUid')?.value) {
      this.form.patchValue(
        {
          productUid: null,
        },
        { emitEvent: false },
      );
    }

    this.cdr.markForCheck();
  }

  // Single screen mode - no step management needed
  // Fields are shown conditionally based on form state

  constructor(
    public readonly formService: CreateFundDialogFormService,
    private readonly formErrorService: FormErrorService,
    private readonly dialog: MatDialogRef<CreateFundDialogComponent>,
    private readonly matDialog: MatDialog,
    private readonly fundDDQRunService: FundDDQRunService,
    private readonly snackBarService: SnackBarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly organizationQuery: OrganizationQuery,
    private readonly productQuery: ProductQuery,
    private readonly router: Router,
    private readonly organizationContextService: OrganizationContextService,
    private readonly appStoreQuery: AppStoreQuery,
    @Optional() private readonly clientService: ClientService | null,
    private readonly ddqInvitationNavService: DdqInvitationNavService,
    @Inject(MAT_DIALOG_DATA) public data?: CreateFundDialogData,
  ) {}

  ngOnInit(): void {
    this.formService.setNewInvestorFieldsRequired(false);
    // Pre-fill from Fund DDQ row / list store / header-nav product context (invite flow)
    if (this.data?.fundDDQRun && (this.data?.skipToInviteStep || this.data?.fromProductCreationHeaderNav)) {
      const run = this.data.fundDDQRun;
      this.form.patchValue({
        flowType: 'invite_issuer',
        issuerUid: run.issuerUid,
        issuerName: run.issuerCompanyName,
        issuerSearch: run.issuerCompanyName,
        productUid: run.productUid,
        fundName: run.productName,
        fundSearch: run.productName,
      });
      this.cdr.markForCheck();

      if (this.isActiveInvestorOrganization() && run.issuerUid && run.productUid) {
        this.proceedWithSearch(run.issuerUid, run.productUid, undefined);
      }
    }

    // If opened from product creation header nav without prefill payload, default flow only
    if (this.data?.fromProductCreationHeaderNav && !this.data?.fundDDQRun) {
      this.form.patchValue({
        flowType: 'invite_issuer',
      });
      this.cdr.markForCheck();
    }

    if (this.isActiveInvestorOrganization() && !this.form.get('flowType')?.value) {
      this.form.patchValue({
        flowType: 'invite_issuer',
      });
      this.cdr.markForCheck();
    }

    // Set up reactive issuer search
    const issuerSearchControl = this.form.get('issuerSearch') as FormControl;
    if (issuerSearchControl) {
      this.issuerOptions$ = this.formService.getIssuerOptions$(issuerSearchControl).pipe(untilDestroyed(this));

      // Subscribe to issuer options to check if no results and store free text
      this.issuerOptions$.pipe(untilDestroyed(this)).subscribe((options) => {
        const issuerUid = this.form.get('issuerUid')?.value;
        const currentSearchValue = issuerSearchControl.value;

        // Only process if no issuerUid is set (meaning user didn't select from dropdown)
        if (!issuerUid && typeof currentSearchValue === 'string' && currentSearchValue.trim().length > 0) {
          // If no options returned, store the typed text as issuerName
          if (options.length === 0) {
            this.form.patchValue({ issuerName: currentSearchValue.trim() }, { emitEvent: false });
            this.cdr.markForCheck();
          }
          // If options exist but user hasn't selected, also store the typed text
          else if (options.length > 0) {
            // Check if current value matches any option - if not, it's free text
            const matchesOption = options.some((opt) => opt.name === currentSearchValue.trim());
            if (!matchesOption) {
              this.form.patchValue({ issuerName: currentSearchValue.trim() }, { emitEvent: false });
              this.cdr.markForCheck();
            }
          }
        }
      });

      // Handle value changes: process both object selections and string inputs
      issuerSearchControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
        // If value is an object (selected from autocomplete), extract issuer info
        if (value && typeof value === 'object' && 'uid' in value && 'name' in value) {
          const issuer = value as IssuerSearchResult;
          // Convert control value to string to avoid "object object" display issue
          issuerSearchControl.setValue(issuer.name, { emitEvent: false });
          // Set issuerUid - use setValue on issuerUidControl to trigger valueChanges
          const issuerUidControl = this.form.get('issuerUid') as FormControl;
          if (issuerUidControl) {
            issuerUidControl.setValue(issuer.uid); // This will trigger valueChanges
          }
          this.form.patchValue(
            {
              issuerName: issuer.name,
            },
            { emitEvent: false },
          );
          this.refreshIssuerAdminsForCurrentFund(issuer.uid);
        }
        // If value is a string (user typing), clear issuerUid if it was previously set
        else if (typeof value === 'string') {
          if (value.trim().length === 0) {
            // Empty string - clear everything (triggered by clear button or manual deletion)
            this.clearIssuerData();
          } else {
            // User is typing - clear issuerUid so free text can be used
            const issuerUid = this.form.get('issuerUid')?.value;
            if (issuerUid) {
              this.form.patchValue({ issuerUid: null }, { emitEvent: false });
            }
          }
        } else if (!value || value === null || value === '') {
          // Null/undefined/empty - clear everything (triggered by clear button)
          this.clearIssuerData();
        }
        this.cdr.markForCheck();
      });
    }

    // Set up fund search
    const issuerUidControl = this.form.get('issuerUid') as FormControl;
    const fundSearchControl = this.form.get('fundSearch') as FormControl;
    const flowTypeControl = this.form.get('flowType') as FormControl;
    if (issuerUidControl && fundSearchControl) {
      this.fundOptions$ = this.formService
        .getFundOptions$(issuerUidControl, fundSearchControl, flowTypeControl)
        .pipe(untilDestroyed(this));

      // Track fund search results to show fundType fields when no products found
      this.fundOptions$.pipe(untilDestroyed(this)).subscribe((options) => {
        const productUid = this.form.get('productUid')?.value;
        const fundName = (this.form.get('fundName')?.value as string | null) || '';
        const keepFundTypeVisibleForInvite = this.shouldKeepFundTypeVisibleForInvite();

        // Show fundType fields if:
        // 1. No productUid is set (not selected from dropdown)
        // 2. Search returned 0 results
        // 3. User has entered a fund name (free text)
        if (!productUid && options.length === 0 && fundName.trim().length > 0) {
          this.showFundTypeFields = true;
          this.ensureDirectDealFundTypeDefault();
        } else if (productUid || options.length > 0) {
          this.showFundTypeFields = keepFundTypeVisibleForInvite;
          if (productUid && !keepFundTypeVisibleForInvite) {
            this.form.patchValue(
              {
                fundType: null,
                fundSubtype: null,
                keepPrivate: false,
                clientCompany: null,
              },
              { emitEvent: false },
            );
          }
        }
        this.cdr.markForCheck();
      });

      // Fund search is always enabled - backend supports searching with or without issuerUid
      // The search will use issuerUid if available, otherwise search all funds
      fundSearchControl.enable();

      // Handle fund search value changes
      fundSearchControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
        // If value is an object (selected from autocomplete), extract fund info
        if (value && typeof value === 'object' && 'productUid' in value) {
          const fund = value as FundSearchResult;
          fundSearchControl.setValue(fund.productName, { emitEvent: false });
          this.form.patchValue(
            {
              productUid: fund.productUid,
              issuerUid: fund.issuerUid,
              issuerCompanyUid: fund.issuerCompanyUid || null,
              fundName: fund.productName,
            },
            { emitEvent: false },
          );
          this.showFundTypeFields = this.shouldKeepFundTypeVisibleForInvite();
        }
        // If value is a string (user typing), it's free text input
        else if (typeof value === 'string') {
          if (value.trim().length > 0) {
            const productUid = this.form.get('productUid')?.value;
            if (!productUid) {
              const trimmedValue = value.trim();
              this.form.patchValue({ fundName: trimmedValue }, { emitEvent: false });
              // Sync validation state: update fundName with the same value to trigger its validation
              const fundNameControl = this.form.get('fundName') as FormControl;
              if (fundNameControl) {
                fundNameControl.setValue(trimmedValue, { emitEvent: false });
                // Mark fundName as touched when user types in fundSearch to show validation errors
                if (fundSearchControl.touched) {
                  fundNameControl.markAsTouched();
                }
              }
            }
          } else {
            // Empty string - clear everything (triggered by clear button or manual deletion)
            this.clearFundData();
          }
        } else if (!value || value === null || value === '') {
          // Null/undefined/empty - clear everything (triggered by clear button)
          this.clearFundData();
        }
        this.cdr.markForCheck();
      });
    }

    // Handle fundType changes to enable/disable fundSubtype
    const fundTypeControl = this.form.get('fundType');
    const fundSubtypeControl = this.form.get('fundSubtype');
    const newInvestorUserTypeControl = this.form.get('newInvestorUserType');
    if (fundTypeControl && fundSubtypeControl) {
      fundSubtypeControl.disable();
      fundTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe((fundType) => {
        if (fundType) {
          this.formService.selectAssetClass(fundType);
          setTimeout(() => {
            fundSubtypeControl.enable();
          }, 100);
        } else {
          fundSubtypeControl.disable();
          fundSubtypeControl.setValue(null);
        }
        if (fundType !== DIRECT_DEAL_ASSET_CLASS) {
          this.form.patchValue({ keepPrivate: false, clientCompany: null }, { emitEvent: false });
        }
        this.cdr.markForCheck();
      });

      // Invite flows only: when subtype is chosen for a new fund, create via create-by-issuer
      // so the issuer-admin invite block can load. Create-later flows wait for the Create button.
      fundSubtypeControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((subtype) => {
        if (subtype) {
          this.prepareFundForInvestorInviteIfNeeded();
        }
        this.cdr.markForCheck();
      });
    }

    if (newInvestorUserTypeControl) {
      newInvestorUserTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.formService.setNewInvestorFieldsRequired(
          this.shouldRequireNewInvestorIdentityFields(),
          this.shouldRequireUserTypeForNewIdentity(),
        );
        this.cdr.markForCheck();
      });
    }

    // Watch form changes to trigger change detection for conditional field visibility
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });

    // Sync issuer admin card state with dropdown choice.
    const issuerAdminEmailChoiceControl = this.form.get('issuerAdminEmailChoice') as FormControl;
    const issuerAdminEmailControl = this.form.get('issuerAdminEmail') as FormControl;

    this.issuerAdminEmailOptions$ = combineLatest([
      issuerAdminEmailControl.valueChanges.pipe(startWith(issuerAdminEmailControl.value)),
      this.issuerAdminsListSubject.asObservable(),
    ]).pipe(
      debounceTime(150),
      map(([emailValue, admins]) => {
        let query = '';
        if (typeof emailValue === 'string') {
          query = emailValue.trim();
        } else if (emailValue && typeof emailValue === 'object' && 'email' in emailValue) {
          query = (emailValue as IssuerAdminSearchResult).email?.trim() ?? '';
        }

        const uniqueByEmail = new Map<string, IssuerAdminSearchResult>();

        if (!query) {
          for (const admin of admins || []) {
            const adminEmail = (admin.email ?? '').trim();
            if (!adminEmail) continue;
            uniqueByEmail.set(adminEmail.toLowerCase(), admin);
          }
          return Array.from(uniqueByEmail.values());
        }

        const normalized = query.toLowerCase();
        for (const admin of admins || []) {
          const adminEmail = (admin.email ?? '').trim();
          if (!adminEmail) continue;
          if (adminEmail.toLowerCase().includes(normalized)) {
            uniqueByEmail.set(adminEmail.toLowerCase(), admin);
          }
        }

        return Array.from(uniqueByEmail.values());
      }),
    );

    if (issuerAdminEmailChoiceControl) {
      issuerAdminEmailChoiceControl.valueChanges.pipe(untilDestroyed(this)).subscribe((choice) => {
        // Don't disturb anything until we have list results.
        if (!this.hasSearchedIssuerAdmin) {
          return;
        }

        if (choice === '') {
          this.issuerAdminResult = null;
          this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
          // Placeholder selected -> no invite input, no warning.
          this.form.patchValue({ issuerAdminEmail: '' }, { emitEvent: false });
          this.showInvestorInviteWarning = false;
          this.updateInvestorInviteWarningFromTypedEmail();
          this.cdr.markForCheck();
          return;
        }

        if (choice === this.inviteNewAdminChoiceValue) {
          // Invite new admin selected -> show input for user to type.
          this.issuerAdminResult = null;
          this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
          // Clear any previous selected/typed value so user starts fresh.
          this.form.patchValue({ issuerAdminEmail: '' }, { emitEvent: false });
          this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
          this.updateInvestorInviteWarningFromTypedEmail();
          this.cdr.markForCheck();
          return;
        }

        // Existing admin email selected.
        const matchedAdmin = this.issuerAdminsList.find((u) => u.email === choice) || null;
        this.issuerAdminResult = matchedAdmin;
        this.form.patchValue({ issuerAdminUserUid: matchedAdmin?.emittentUserUid ?? null }, { emitEvent: false });
        this.form.patchValue({ issuerAdminEmail: matchedAdmin?.email ?? choice }, { emitEvent: false });
        this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
        this.showInvestorInviteWarning = false;
        this.cdr.markForCheck();
      });
    }

    if (issuerAdminEmailControl) {
      issuerAdminEmailControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((value) => {
        const emailControl = this.form.get('issuerAdminEmail') as FormControl;

        const shouldHideEmailValidation =
          this.isActiveInvestorOrganization() &&
          isInviteFlow(this.form.get('flowType')?.value) &&
          !!this.form.get('productUid')?.value;

        if (shouldHideEmailValidation) {
          emailControl.markAsUntouched({ emitEvent: false });
        }

        if (value && typeof value === 'object' && 'email' in value) {
          const admin = value as IssuerAdminSearchResult;
          const email = (admin.email ?? '').trim();
          if (!email) {
            this.issuerAdminResult = null;
            this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
            this.showInvestorInviteWarning = false;
            this.cdr.markForCheck();
            return;
          }

          this.issuerAdminResult = admin;
          this.form.patchValue(
            {
              issuerAdminUserUid: admin.emittentUserUid ?? null,
              issuerAdminEmail: email,
            },
            { emitEvent: false },
          );
          this.showInvestorInviteWarning = false;
          this.cdr.markForCheck();
          return;
        }

        const normalizedEmail = typeof value === 'string' ? value.trim() : '';

        if (!normalizedEmail) {
          this.issuerAdminResult = null;
          this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
          this.showInvestorInviteWarning = false;
          this.cdr.markForCheck();
          return;
        }

        // Keep "Issuer Admin Found" card in sync with typed email (exact match).
        if (emailControl.valid === true) {
          const matchedAdmin =
            this.issuerAdminsList.find((u) => (u.email ?? '').toLowerCase() === normalizedEmail.toLowerCase()) || null;
          this.issuerAdminResult = matchedAdmin;
          this.form.patchValue({ issuerAdminUserUid: matchedAdmin?.emittentUserUid ?? null }, { emitEvent: false });
        } else {
          this.issuerAdminResult = null;
          this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
        }

        this.updateInvestorInviteWarningFromTypedEmail();
        this.cdr.markForCheck();
      });
    }
  }

  private isActiveInvestorOrganization(): boolean {
    const activeOrganization = this.organizationQuery.getActive();
    // `getActive()` typing may be narrower than the possible organization types.
    const orgType = activeOrganization?.type as unknown as string | undefined;
    return orgType === 'investor' || orgType === 'client';
  }

  private shouldKeepFundTypeVisibleForInvite(): boolean {
    const flowType = this.form.get('flowType')?.value as FlowType | null;
    const fundType = this.form.get('fundType')?.value;
    const fundSubtype = this.form.get('fundSubtype')?.value;
    return this.isActiveInvestorOrganization() && isInviteFlow(flowType) && !!fundType && !!fundSubtype;
  }

  private shouldHandleIssuerAdminFlowForSelectedMode(): boolean {
    if (!this.isActiveInvestorOrganization()) {
      return false;
    }

    // Issuer-admin invite UI + early create-by-issuer only apply to Invite flows.
    // Create-later flows create on the Create button and never show the invite container.
    const flowType = this.form.get('flowType')?.value as FlowType | null;
    return isInviteFlow(flowType);
  }

  private ensureDirectDealFundTypeDefault(): void {
    const flowType = this.form.get('flowType')?.value as FlowType | null;
    if (!isDirectDealFlow(flowType)) {
      return;
    }
    if (this.form.get('fundType')?.value === DIRECT_DEAL_ASSET_CLASS) {
      return;
    }
    this.form.patchValue(
      {
        fundType: DIRECT_DEAL_ASSET_CLASS,
        fundSubtype: null,
        keepPrivate: false,
        clientCompany: null,
      },
      { emitEvent: false },
    );
    this.formService.selectAssetClass(DIRECT_DEAL_ASSET_CLASS);
    const fundSubtypeControl = this.form.get('fundSubtype');
    if (fundSubtypeControl) {
      setTimeout(() => fundSubtypeControl.enable(), 100);
    }
  }

  private getLoggedInClientUid(): string | null {
    const state = this.appStoreQuery.getValue();
    return state?.userProfile?.client?.uid ?? state?.user?.client?.uid ?? null;
  }

  private resolveKeepPrivateClientCompany(): string | number | null {
    return resolveClientCompanyForKeepPrivate(
      !!this.form.get('keepPrivate')?.value,
      this.form.get('clientCompany')?.value,
      this.router.url || '',
      this.getLoggedInClientUid(),
    );
  }

  private shouldRequireNewInvestorIdentityFields(): boolean {
    return this.isNewInvestor || this.showInvestorInviteWarning;
  }

  private shouldRequireUserTypeForNewIdentity(): boolean {
    if (this.showInvestorInviteWarning && !this.isNewInvestor) {
      return false;
    }
    return true;
  }

  /**
   * For investor invite flow, ensure productUid/issuerUid exist before showing invite-admin block.
   * - issuerUid present + productUid missing -> create-by-issuer path
   * - issuerUid missing + productUid missing -> create path (requires issuerName)
   */
  private prepareFundForInvestorInviteIfNeeded(): void {
    if (!this.isActiveInvestorOrganization()) {
      return;
    }

    if (!this.shouldHandleIssuerAdminFlowForSelectedMode()) {
      return;
    }

    const productUid = this.form.get('productUid')?.value as string | null;
    if (productUid) {
      return;
    }

    if (this.isPreparingInviteAdminFund || this.isLoadingIssuerAdmin) {
      return;
    }

    const issuerUid = this.form.get('issuerUid')?.value as string | null;
    const issuerName = (this.form.get('issuerName')?.value as string | null) ?? null;
    const fundName = (this.form.get('fundName')?.value as string | null) ?? null;
    const fundType = this.form.get('fundType')?.value as string | null;
    const fundSubtype = this.form.get('fundSubtype')?.value as string | null;

    if (!fundName || fundName.trim().length === 0) {
      return;
    }

    if (!this.showFundTypeFields || !fundType || !fundSubtype) {
      return;
    }

    if (!issuerUid && (!issuerName || issuerName.trim().length === 0)) {
      return;
    }

    this.isPreparingInviteAdminFund = true;
    this.isLoadingIssuerAdmin = true;
    this.cdr.markForCheck();

    const request: CreateFundDDQRunRequest = this.buildCreateFundRequestFromForm();

    this.fundDDQRunService
      .createFundDDQRun(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (run) => {
          this.isPreparingInviteAdminFund = false;
          this.isLoadingIssuerAdmin = false;
          this.form.patchValue(
            {
              issuerUid: run.issuerUid,
              issuerName: run.issuerCompanyName,
              productUid: run.productUid,
              fundName: run.productName,
              fundSearch: run.productName,
            },
            { emitEvent: false },
          );
          this.showFundTypeFields = true;
          this.proceedWithSearch(run.issuerUid, run.productUid, undefined);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isPreparingInviteAdminFund = false;
          this.isLoadingIssuerAdmin = false;
          this.formErrorService.onServerError(error, this.form);
          this.cdr.markForCheck();
        },
      });
  }

  private buildCreateFundRequestFromForm(): CreateFundDDQRunRequest {
    const keepPrivate = !!this.form.get('keepPrivate')?.value;
    const clientCompany = this.resolveKeepPrivateClientCompany();
    // Use getRawValue so disabled controls (e.g. locked Direct Deal fundType) are included in the API payload.
    const request = this.formService.toModel({
      ...this.form.getRawValue(),
      ...(keepPrivate && clientCompany ? { clientCompany: String(clientCompany) } : {}),
    });
    return this.data?.clientUid ? { ...request, clientUid: this.data.clientUid } : request;
  }

  private updateInvestorInviteWarningFromTypedEmail(): void {
    if (!this.hasSearchedIssuerAdmin) {
      this.showInvestorInviteWarning = false;
      this.formService.setNewInvestorFieldsRequired(
        this.shouldRequireNewInvestorIdentityFields(),
        this.shouldRequireUserTypeForNewIdentity(),
      );
      return;
    }

    const emailControl = this.form.get('issuerAdminEmail') as FormControl;
    const rawValue = emailControl?.value;
    const emailValue =
      typeof rawValue === 'string'
        ? rawValue.trim()
        : rawValue && typeof rawValue === 'object' && 'email' in rawValue
        ? (rawValue as IssuerAdminSearchResult).email?.trim() ?? ''
        : '';

    if (!emailValue || emailControl?.valid !== true) {
      this.showInvestorInviteWarning = false;
      this.formService.setNewInvestorFieldsRequired(
        this.shouldRequireNewInvestorIdentityFields(),
        this.shouldRequireUserTypeForNewIdentity(),
      );
      return;
    }

    const matched = this.issuerAdminsList.some((u) => (u.email ?? '').toLowerCase() === emailValue.toLowerCase());
    this.showInvestorInviteWarning = !matched;
    this.formService.setNewInvestorFieldsRequired(
      this.shouldRequireNewInvestorIdentityFields(),
      this.shouldRequireUserTypeForNewIdentity(),
    );
  }

  /**
   * Clear all issuer-related data (called when clear icon is clicked or field is cleared)
   */
  clearIssuerData(): void {
    this.form.patchValue(
      {
        issuerUid: null,
        issuerCompanyUid: null,
        issuerName: null,
        issuerSearch: null,
      },
      { emitEvent: false },
    );
    // Also clear the search control explicitly
    const issuerSearchControl = this.form.get('issuerSearch') as FormControl;
    if (issuerSearchControl) {
      issuerSearchControl.setValue(null, { emitEvent: false });
    }

    // Reset issuer-admin state (fund might change after clearing issuer)
    this.issuerAdminsList = [];
    this.issuerAdminsListSubject.next([]);
    this.issuerAdminResult = null;
    this.hasSearchedIssuerAdmin = false;
    this.showInvestorInviteWarning = false;
    this.form.patchValue(
      { issuerAdminUserUid: null, issuerAdminEmailChoice: '', issuerAdminEmail: '' },
      { emitEvent: false },
    );
    this.issuerAdminEmailChoiceOptions = [
      new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
      new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
    ];
    this.cdr.markForCheck();
  }

  /**
   * Handle issuer selection from autocomplete
   */
  onIssuerSelected(issuer?: IssuerSearchResult): void {
    if (issuer && typeof issuer === 'object' && issuer.uid && issuer.name) {
      // Set issuerUid using setValue to trigger valueChanges (needed for fund search)
      const issuerUidControl = this.form.get('issuerUid') as FormControl;
      if (issuerUidControl) {
        issuerUidControl.setValue(issuer.uid); // This will trigger valueChanges
      }

      // Set issuerName
      this.form.patchValue(
        {
          issuerName: issuer.name,
        },
        { emitEvent: false },
      );

      // Convert control value to string to avoid "object object" display issue
      const issuerSearchControl = this.form.get('issuerSearch') as FormControl;
      if (issuerSearchControl) {
        issuerSearchControl.setValue(issuer.name, { emitEvent: false });
      }

      this.refreshIssuerAdminsForCurrentFund(issuer.uid);
      this.cdr.markForCheck();
    }
  }

  /**
   * In investor invite flow, if fund is already selected and issuer changes,
   * refresh issuer-admin list for the new issuer + current fund combination.
   */
  private refreshIssuerAdminsForCurrentFund(nextIssuerUid: string): void {
    if (!this.shouldHandleIssuerAdminFlowForSelectedMode()) {
      return;
    }

    const productUid = this.form.get('productUid')?.value as string | null;
    if (!productUid) {
      return;
    }

    this.proceedWithSearch(nextIssuerUid, productUid, undefined);
  }

  /**
   * Handle issuer admin selection from autocomplete (existing admin email).
   * Keeps invite button logic intact by setting issuerAdminUserUid and issuerAdminResult.
   */
  onIssuerAdminEmailSelected(admin?: IssuerAdminSearchResult): void {
    if (!admin || !admin.email) {
      this.issuerAdminResult = null;
      this.form.patchValue({ issuerAdminUserUid: null }, { emitEvent: false });
      this.showInvestorInviteWarning = false;
      this.cdr.markForCheck();
      return;
    }

    this.hasSearchedIssuerAdmin = true; // selection implies we have searched/loaded admin info
    this.issuerAdminResult = admin;
    this.form.patchValue(
      {
        issuerAdminUserUid: admin.emittentUserUid ?? null,
        issuerAdminEmail: admin.email,
      },
      { emitEvent: false },
    );
    this.showInvestorInviteWarning = false;
    this.cdr.markForCheck();
  }

  /**
   * Clear all fund-related data (called when clear icon is clicked or field is cleared)
   */
  clearFundData(): void {
    this.form.patchValue(
      {
        productUid: null,
        fundName: null,
        fundSearch: null,
        fundType: null,
        fundSubtype: null,
        keepPrivate: false,
        clientCompany: null,
      },
      { emitEvent: false },
    );
    // Also clear the search control explicitly
    const fundSearchControl = this.form.get('fundSearch') as FormControl;
    if (fundSearchControl) {
      fundSearchControl.setValue(null, { emitEvent: false });
    }
    // Hide fund type fields when clearing
    this.showFundTypeFields = false;

    // Reset issuer-admin state tied to selected fund
    this.issuerAdminsList = [];
    this.issuerAdminsListSubject.next([]);
    this.issuerAdminResult = null;
    this.hasSearchedIssuerAdmin = false;
    this.showInvestorInviteWarning = false;
    this.form.patchValue(
      { issuerAdminUserUid: null, issuerAdminEmailChoice: '', issuerAdminEmail: '' },
      { emitEvent: false },
    );
    this.issuerAdminEmailChoiceOptions = [
      new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
      new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
    ];
    this.cdr.markForCheck();
  }

  /**
   * Handle fund selection from autocomplete
   */
  onFundSelected(fund: FundSearchResult): void {
    if (fund && typeof fund === 'object' && fund.productUid) {
      this.form.patchValue(
        {
          productUid: fund.productUid,
          issuerUid: fund.issuerUid,
          issuerCompanyUid: fund.issuerCompanyUid || null,
          fundName: fund.productName,
        },
        { emitEvent: false },
      );

      const fundSearchControl = this.form.get('fundSearch') as FormControl;
      if (fundSearchControl) {
        fundSearchControl.setValue(fund.productName, { emitEvent: false });
      }

      // Investor flow: after selecting a fund, fetch all issuer admins for dropdown.
      if (this.shouldHandleIssuerAdminFlowForSelectedMode()) {
        const issuerUid = fund.issuerUid;
        const productUid = fund.productUid;

        if (issuerUid && productUid) {
          this.issuerAdminsList = [];
          this.issuerAdminsListSubject.next([]);
          this.issuerAdminResult = null;
          this.hasSearchedIssuerAdmin = false;
          this.showInvestorInviteWarning = false;
          this.issuerAdminEmailChoiceOptions = [
            new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
            new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
          ];
          this.form.patchValue({ issuerAdminUserUid: null, issuerAdminEmailChoice: '' }, { emitEvent: false });
          this.proceedWithSearch(issuerUid, productUid, undefined);
        }
      }

      this.cdr.markForCheck();
    }
  }

  /**
   * Display function for issuer autocomplete
   */
  displayIssuerFn = (issuer: IssuerSearchResult | string | null): string => {
    if (!issuer) {
      return '';
    }
    if (typeof issuer === 'string') {
      return issuer;
    }
    // Ensure we return the name property correctly
    if (issuer && typeof issuer === 'object' && 'name' in issuer) {
      return issuer.name || '';
    }
    return '';
  };

  /**
   * Display function for fund autocomplete
   */
  displayFundFn = (fund: FundSearchResult | string | null): string => {
    if (!fund) {
      return '';
    }
    if (typeof fund === 'string') {
      return fund;
    }
    if (fund && typeof fund === 'object' && 'productName' in fund) {
      return fund.productName || '';
    }
    return '';
  };

  /**
   * Display function for issuer admin email autocomplete.
   */
  displayIssuerAdminEmailFn = (issuerAdmin: IssuerAdminSearchResult | string | null): string => {
    if (!issuerAdmin) {
      return '';
    }
    if (typeof issuerAdmin === 'string') {
      return issuerAdmin;
    }
    return issuerAdmin.email || '';
  };

  /**
   * Check if form is ready for Create Fund action (create_fund_later flow)
   */
  isFormReadyForCreate(): boolean {
    const flowType = this.form.get('flowType')?.value as FlowType | null;
    if (!isCreateFlow(flowType)) {
      return false;
    }

    // Require issuer (either UID or name)
    const issuerUid = this.form.get('issuerUid')?.value;
    const issuerName = this.form.get('issuerName')?.value;
    if (!issuerUid && !issuerName) {
      return false;
    }

    // Require fund name
    const fundName = this.form.get('fundName')?.value;
    if (!fundName) {
      return false;
    }

    if (!this.isKeepPrivateReadyForSubmit()) {
      return false;
    }

    // If productUid exists (selected from dropdown), that's sufficient
    const productUid = this.form.get('productUid')?.value;
    if (productUid) {
      return true;
    }

    const fundType = this.form.get('fundType')?.value;
    const fundSubtype = this.form.get('fundSubtype')?.value;

    // Direct Deal create always requires type (defaulted) + subtype for new funds
    if (isDirectDealFlow(flowType)) {
      return !!(fundType && fundSubtype);
    }

    // If showFundTypeFields is true (no products found), require fundType and fundSubtype
    if (this.showFundTypeFields) {
      return !!(fundType && fundSubtype);
    }

    return true;
  }

  /**
   * Keep Private requires client_company from logged-in client user or LP client dashboard URL.
   */
  private isKeepPrivateReadyForSubmit(): boolean {
    if (!this.form.get('keepPrivate')?.value) {
      return true;
    }
    return this.resolveKeepPrivateClientCompany() != null;
  }

  /**
   * Check if form is ready for Invite action (invite_issuer flow)
   */
  isFormReadyForInvite(): boolean {
    // For emittent organization (GP invite), require valid email and search response
    const activeOrganization = this.organizationQuery.getActive();
    const isEmittent = activeOrganization?.type === 'emittent';

    if (isEmittent) {
      const email = this.form.get('issuerAdminEmail')?.value;
      const emailValid = this.form.get('issuerAdminEmail')?.valid;
      // Require valid email AND search must have been performed
      // Search response will either show existing investor result or new investor fields
      const hasBaseInviteData = !!(email && emailValid && this.hasSearchedIssuerAdmin);
      if (!hasBaseInviteData) {
        return false;
      }

      if (!this.isNewInvestor) {
        return true;
      }
      return this.formService.hasValidNewInvestorRequiredFields();
    }

    // For investor organization, require invite flow type
    const flowType = this.form.get('flowType')?.value as FlowType | null;
    if (!isInviteFlow(flowType)) {
      return false;
    }

    // For investor organization (LP invite), require issuer and fund info
    // Require issuer UID
    const issuerUid = this.form.get('issuerUid')?.value;
    if (!issuerUid) {
      return false;
    }

    // Require either productUid (existing fund) OR fundName + fundType + fundSubtype (new fund)
    const productUid = this.form.get('productUid')?.value;
    const fundName = this.form.get('fundName')?.value;
    const fundType = this.form.get('fundType')?.value;
    const fundSubtype = this.form.get('fundSubtype')?.value;

    // If productUid exists, fund is ready
    if (productUid) {
      // Require either userUid (from search) or valid email AND search must have been performed
      const userUid = this.form.get('issuerAdminUserUid')?.value;
      const email = this.form.get('issuerAdminEmail')?.value;
      const emailValid = this.form.get('issuerAdminEmail')?.valid;
      const hasBaseInviteData = !!(this.hasSearchedIssuerAdmin && (userUid || (email && emailValid)));
      if (!hasBaseInviteData) {
        return false;
      }
      if (this.showInvestorInviteWarning) {
        return this.formService.hasValidNewInvestorRequiredFields(false);
      }
      return true;
    }

    // New fund create-then-invite: Keep Private needs client context when enabled
    if (!this.isKeepPrivateReadyForSubmit()) {
      return false;
    }

    // New fund: require name + type + subtype (Direct Deal type is defaulted; subtype still required)
    if (this.showFundTypeFields || isDirectDealFlow(flowType)) {
      if (!fundName || !fundType || !fundSubtype) {
        return false;
      }
      // For new fund, also require email AND search must have been performed
      const email = this.form.get('issuerAdminEmail')?.value;
      const emailValid = this.form.get('issuerAdminEmail')?.valid;
      return !!(email && emailValid && this.hasSearchedIssuerAdmin);
    }

    return false;
  }

  // Step navigation methods removed - single screen mode doesn't need them

  /**
   * Search issuer admin
   * If no productUid exists but fundType/fundSubtype are filled, creates the fund first
   * For emittent organization, gets productUid from active product in store
   */
  searchIssuerAdmin(): void {
    const email = this.form.get('issuerAdminEmail')?.value as string;

    // Check if this is emittent organization
    const activeOrganization = this.organizationQuery.getActive();
    const isEmittent = activeOrganization?.type === 'emittent';

    if (isEmittent) {
      if (!email) {
        this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
        return;
      }
      this.verifyInvestorForEmittent(email.trim());
      return;
    }

    const issuerUid = this.form.get('issuerUid')?.value;
    const productUid = this.form.get('productUid')?.value;

    if (!productUid && this.showFundTypeFields) {
      if (!email) {
        this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
        return;
      }
      const fundName = this.form.get('fundName')?.value;
      const fundType = this.form.get('fundType')?.value;
      const fundSubtype = this.form.get('fundSubtype')?.value;

      if (!fundName || !fundType || !fundSubtype) {
        this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
        return;
      }

      this.isLoadingIssuerAdmin = true;
      this.cdr.markForCheck();

      const request: CreateFundDDQRunRequest = this.buildCreateFundRequestFromForm();

      this.fundDDQRunService
        .createFundDDQRun(request)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (run) => {
            this.form.patchValue(
              {
                issuerUid: run.issuerUid,
                issuerName: run.issuerCompanyName,
                productUid: run.productUid,
                fundName: run.productName,
                fundSearch: run.productName,
              },
              { emitEvent: false },
            );
            this.showFundTypeFields = true;
            this.cdr.markForCheck();

            this.proceedWithSearch(run.issuerUid, run.productUid, email);
          },
          error: (error) => {
            this.isLoadingIssuerAdmin = false;
            console.error('Error creating fund before search:', error);
            this.formErrorService.onServerError(error, this.form);
            this.cdr.markForCheck();
          },
        });

      return;
    }

    if (!productUid) {
      this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
      return;
    }

    this.proceedWithSearch(issuerUid, productUid, email);
  }

  /**
   * Verify investor for emittent organization
   * Uses /api/subscriptions/verify/investor/ endpoint
   * Searches by email only (investor_name is sent as empty string)
   */
  private verifyInvestorForEmittent(investorEmail: string): void {
    this.isLoadingIssuerAdmin = true;
    this.isNewInvestor = false; // Reset flag
    this.hasSearchedIssuerAdmin = false; // Reset search flag
    this.cdr.markForCheck();

    this.fundDDQRunService
      .verifyInvestor({
        investor_name: '', // Search by email only
        investor_email: investorEmail,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.isLoadingIssuerAdmin = false;
          this.hasSearchedIssuerAdmin = true; // Mark search as performed

          // Check if investor/client exists - response may include matchingInvestors and/or matchingClients arrays
          const exists = response?.exists === true;
          const matchingInvestor =
            response?.matchingInvestors && response.matchingInvestors.length > 0 ? response.matchingInvestors[0] : null;
          const matchingClient =
            response?.matchingClients && response.matchingClients.length > 0 ? response.matchingClients[0] : null;
          const matchingEntity = matchingInvestor || matchingClient;

          if (exists && matchingEntity) {
            // Existing investor/client - map response to IssuerAdminSearchResult format
            this.isNewInvestor = false;
            this.issuerAdminResult = {
              issuerUid: matchingEntity.uid || response.investorUid || response.investor_uid || '',
              issuerCompanyName: matchingEntity.fullCompanyName || response.companyName || response.company_name || '',
              emittentUserUid: response.userUid || response.user_uid || '', // May not be in new response structure
              userFullName: response.userFullName || response.user_full_name || '', // May not be in new response structure
              email: matchingEntity.email || response.investorEmail || response.investor_email || investorEmail,
              investorType:
                matchingEntity.investorType ||
                matchingEntity.clientType ||
                response.investorType ||
                response.investor_type,
              fullCompanyName:
                matchingEntity.fullCompanyName ||
                response.fullCompanyName ||
                response.full_company_name ||
                response.companyName ||
                response.company_name,
            };

            // Set userUid in form if available
            if (this.issuerAdminResult.emittentUserUid) {
              this.form.patchValue({
                issuerAdminUserUid: this.issuerAdminResult.emittentUserUid,
              });
            }

            // Clear new investor fields
            this.form.patchValue({
              newInvestorSalutation: '',
              newInvestorFirstName: '',
              newInvestorLastName: '',
              newInvestorUserType: null,
              newInvestorCompanyName: '',
            });
            this.formService.setNewInvestorFieldsRequired(false);
          } else {
            // New investor - show additional fields
            this.isNewInvestor = true;
            this.issuerAdminResult = null;
            this.formService.setNewInvestorFieldsRequired(true);

            // Clear issuerAdminUserUid since this is a new investor
            this.form.patchValue({
              issuerAdminUserUid: null,
            });

            // Pre-fill email in new investor fields if available
            // Note: firstName, lastName, userType, companyName will be empty for user to fill
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoadingIssuerAdmin = false;
          this.hasSearchedIssuerAdmin = false; // Reset on error
          this.isNewInvestor = false;
          this.formService.setNewInvestorFieldsRequired(false);
          this.issuerAdminResult = null;
          console.error('Error verifying investor:', error);
          this.snackBarService.error('INTL_ERROR_VERIFYING_INVESTOR');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Proceed with searching issuer admin (called after fund creation if needed)
   */
  private proceedWithSearch(issuerUid: string, productUid: string, emailToMatch?: string): void {
    this.isLoadingIssuerAdmin = true;
    this.hasSearchedIssuerAdmin = false; // Reset search flag
    this.showInvestorInviteWarning = false;
    this.issuerAdminsList = [];
    this.issuerAdminsListSubject.next([]);
    this.issuerAdminResult = null;
    this.form.patchValue(
      { issuerAdminUserUid: null, issuerAdminEmailChoice: '', issuerAdminEmail: '' },
      {
        emitEvent: false,
      },
    );
    this.issuerAdminEmailChoiceOptions = [
      new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
      new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
    ];
    this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();

    this.fundDDQRunService
      // Investor flow: fetch all admins for issuer+product (dropdown will handle selection / invite-new-admin mode)
      .searchIssuerAdmins({ issuerUid, productUid })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (results) => {
          this.isLoadingIssuerAdmin = false;
          this.hasSearchedIssuerAdmin = true; // Mark search as performed

          this.issuerAdminsList = results || [];
          this.issuerAdminsListSubject.next(this.issuerAdminsList);

          // Build dropdown options: placeholder, all admin emails, then "Invite New Admin".
          const uniqueEmails = Array.from(
            new Set(
              this.issuerAdminsList
                .map((u) => u.email)
                .filter((emailStr): emailStr is string => typeof emailStr === 'string' && emailStr.trim().length > 0),
            ),
          );

          this.issuerAdminEmailChoiceOptions = [
            new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
            ...uniqueEmails.map((email) => new FormOption(email, email)),
            new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
          ];

          const explicitEmailProvided = !!emailToMatch && emailToMatch.trim().length > 0;

          // Only auto-select when caller explicitly provides `emailToMatch`.
          // For "auto fetch after fund selection" we keep default on placeholder (no invite input yet).
          const emailToMatchNormalized = (emailToMatch ?? '').trim().toLowerCase();
          const matchingAdmin =
            emailToMatchNormalized.length > 0
              ? this.issuerAdminsList.find((u) => (u.email ?? '').toLowerCase() === emailToMatchNormalized) || null
              : null;

          if (matchingAdmin) {
            this.issuerAdminResult = matchingAdmin;
            this.form.patchValue(
              {
                issuerAdminUserUid: matchingAdmin.emittentUserUid,
                issuerAdminEmailChoice: matchingAdmin.email,
                // Keep existing email control valid for inviteIssuerAdmin()
                issuerAdminEmail: matchingAdmin.email,
              },
              { emitEvent: false },
            );
            this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
            this.showInvestorInviteWarning = false;
          } else {
            this.issuerAdminResult = null;
            if (explicitEmailProvided) {
              // If user clicked Search with an explicit email and it wasn't found,
              // switch to "Invite New Admin" mode and keep the email for editing.
              const normalizedOriginalEmail = emailToMatch.trim();
              this.form.patchValue(
                {
                  issuerAdminUserUid: null,
                  issuerAdminEmailChoice: this.inviteNewAdminChoiceValue,
                  issuerAdminEmail: normalizedOriginalEmail,
                },
                { emitEvent: false },
              );
              this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
              this.showInvestorInviteWarning = true;
            } else {
              // When auto-fetching after fund selection, keep dropdown on placeholder (no input yet).
              this.form.patchValue(
                {
                  issuerAdminUserUid: null,
                  issuerAdminEmailChoice: '',
                  issuerAdminEmail: '',
                },
                { emitEvent: false },
              );
              this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
              this.showInvestorInviteWarning = false;
            }
          }
          this.formService.setNewInvestorFieldsRequired(
            this.shouldRequireNewInvestorIdentityFields(),
            this.shouldRequireUserTypeForNewIdentity(),
          );
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoadingIssuerAdmin = false;
          this.hasSearchedIssuerAdmin = false; // Reset on error
          this.issuerAdminResult = null;
          this.showInvestorInviteWarning = false;
          this.issuerAdminsList = [];
          this.issuerAdminsListSubject.next([]);
          console.error('Error searching issuer admin:', error);
          this.snackBarService.error('INTL_ERROR_SEARCHING_ISSUER_ADMIN');
          this.issuerAdminEmailChoiceOptions = [
            new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
            new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
          ];
          this.form.patchValue(
            { issuerAdminUserUid: null, issuerAdminEmailChoice: '', issuerAdminEmail: '' },
            { emitEvent: false },
          );
          this.formService.setNewInvestorFieldsRequired(
            this.shouldRequireNewInvestorIdentityFields(),
            this.shouldRequireUserTypeForNewIdentity(),
          );
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Invite issuer admin
   * Uses user type (GP/LP) from profile store to determine which endpoint to call
   * Note: Fund creation (if needed) happens in searchIssuerAdmin() when Search button is clicked
   */
  inviteIssuerAdmin(): void {
    const issuerUid = this.form.get('issuerUid')?.value;
    const issuerName = this.form.get('issuerName')?.value;
    const productUid = this.form.get('productUid')?.value;
    const fundName = this.form.get('fundName')?.value;
    const emailFromInput = this.form.get('issuerAdminEmail')?.value as string;
    const emailChoice = this.form.get('issuerAdminEmailChoice')?.value as string;
    const emailFromDropdown = emailChoice === this.inviteNewAdminChoiceValue ? '' : emailChoice;
    const email = (emailFromInput && emailFromInput.trim().length > 0 ? emailFromInput : emailFromDropdown) as string;

    // Ensure the email control is synced for validation.
    if (email && email !== emailFromInput) {
      this.form.patchValue({ issuerAdminEmail: email }, { emitEvent: false });
      this.form.get('issuerAdminEmail')?.updateValueAndValidity({ emitEvent: false });
    }

    // Validate required fields
    if (!email || !email.trim() || !this.form.get('issuerAdminEmail')?.valid) {
      this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
      return;
    }

    // In GP flow, when investor does not exist, first name/last name/user type are required.
    const activeOrganization = this.organizationQuery.getActive();
    const isEmittent = activeOrganization?.type === 'emittent';
    if (isEmittent && this.isNewInvestor && !this.formService.hasValidNewInvestorRequiredFields()) {
      this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
      return;
    }

    // For LP invitations, require either issuerUid or issuerName
    // For GP invitations, these are optional
    // We'll determine organization type first, then validate accordingly

    // Get organization type to determine LP/GP
    this.organizationQuery
      .selectChosenOrganizationType()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((organizationType) => {
        if (!organizationType) {
          this.snackBarService.error('INTL_ERROR_ORGANIZATION_TYPE_NOT_FOUND');
          return;
        }

        // If organization type is 'investor' or 'client', proceed with LP invite
        // If organization type is 'emittent', proceed with GP invite
        const isLP = organizationType === 'investor' || organizationType === 'client';
        const isGP = organizationType === 'emittent';
        const requiresNewIdentity = this.shouldRequireNewInvestorIdentityFields();

        if (!isLP && !isGP) {
          this.snackBarService.error('INTL_ERROR_INVALID_ORGANIZATION_TYPE');
          return;
        }

        if (
          requiresNewIdentity &&
          !this.formService.hasValidNewInvestorRequiredFields(this.shouldRequireUserTypeForNewIdentity())
        ) {
          this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
          return;
        }

        // Proceed with invite based on organization type
        if (isLP) {
          this.proceedWithLPInvite(issuerUid, issuerName, productUid, fundName, email);
        } else {
          this.proceedWithGPInvite(email);
        }
      });
  }

  /**
   * Proceed with LP (Investor) invitation
   */
  private proceedWithLPInvite(
    issuerUid: string | null,
    issuerName: string | null,
    productUid: string | null,
    fundName: string | null,
    email: string,
  ): void {
    // LP invitation: require either issuer_name or existing_issuer_uid
    if (!issuerName && !issuerUid) {
      this.snackBarService.error('INTL_PLEASE_FILL_ALL_FIELDS');
      return;
    }

    const lpRequest: LPInviteRequest = {
      invitee_email: email.trim(),
      invitation_type: 'fillout',
    };

    // Add issuer information (at least one is required)
    if (issuerUid) {
      lpRequest.existing_issuer_uid = issuerUid;
    }
    if (issuerName) {
      lpRequest.issuer_name = issuerName;
    }

    if (this.showInvestorInviteWarning) {
      const salutation = this.form.get('newInvestorSalutation')?.value as ValueObject['codename'] | null;
      const firstName = (this.form.get('newInvestorFirstName')?.value as string | null)?.trim();
      const lastName = (this.form.get('newInvestorLastName')?.value as string | null)?.trim();
      const userType = this.form.get('newInvestorUserType')?.value as 'natural_person' | 'company' | null;
      const companyName = (this.form.get('newInvestorCompanyName')?.value as string | null)?.trim();

      if (salutation) {
        lpRequest.invite_salutation = salutation;
      }
      if (firstName) {
        lpRequest.invite_first_name = firstName;
      }
      if (lastName) {
        lpRequest.invite_last_name = lastName;
      }
      if (userType) {
        lpRequest.invite_user_type = userType;
      }
      if (userType === 'company' && companyName) {
        lpRequest.invite_company_name = companyName;
      }
    }

    // Add optional fields
    if (productUid) {
      lpRequest.product_id = productUid;
    }
    if (fundName) {
      lpRequest.fund_name = fundName;
    }

    this.fundDDQRunService
      .inviteLP(lpRequest)
      .pipe(
        switchMap(() => this.initializeEmittentDefaultTemplates(productUid, issuerUid)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (defaultTemplatesInitialized) => {
          if (!defaultTemplatesInitialized) {
            return;
          }

          this.snackBarService.success('INTL_INVITATION_SENT_SUCCESSFULLY');
          this.close('invitation_sent');
        },
        error: (error) => {
          console.error('Error inviting LP user:', error);
          this.formErrorService.onServerError(error, this.form);
        },
      });
  }

  /**
   * Proceed with GP (Emittent) invitation
   * Uses email, organization type from form controls
   * Gets productId from ProductQuery store (active product)
   */
  private proceedWithGPInvite(email: string): void {
    const activeProduct = this.productQuery.getActive();
    let productId: string | null = null;
    let inviterEmittentCompanyUid: string | null = null;
    if (activeProduct && !Array.isArray(activeProduct) && 'id' in activeProduct) {
      productId = activeProduct.id;
      const companyId = activeProduct.emittentCompany?.id;
      inviterEmittentCompanyUid = companyId?.trim() ? companyId.trim() : null;
    }

    const gpRequest: GPInviteRequest = {
      invitee_email: email.trim(),
      invitation_type: 'fillout',
      invitee_org_type: 'client',
    };

    // For new investors, include invite identity payload.
    if (this.isNewInvestor) {
      const salutation = this.form.get('newInvestorSalutation')?.value as ValueObject['codename'] | null;
      const firstName = (this.form.get('newInvestorFirstName')?.value as string | null)?.trim();
      const lastName = (this.form.get('newInvestorLastName')?.value as string | null)?.trim();
      const userType = this.form.get('newInvestorUserType')?.value as 'natural_person' | 'company' | null;
      const companyName = (this.form.get('newInvestorCompanyName')?.value as string | null)?.trim();

      if (salutation) {
        gpRequest.invite_salutation = salutation;
      }
      if (firstName) {
        gpRequest.invite_first_name = firstName;
      }
      if (lastName) {
        gpRequest.invite_last_name = lastName;
      }
      if (userType) {
        gpRequest.invite_user_type = userType;
      }
      if (userType === 'company' && companyName) {
        gpRequest.invite_company_name = companyName;
      }
    }

    // Add product ID from store if available
    if (productId) {
      gpRequest.product_id = productId;
    }
    if (inviterEmittentCompanyUid) {
      gpRequest.inviter_emittent_company_uid = inviterEmittentCompanyUid;
    }

    this.fundDDQRunService
      .inviteGP(gpRequest)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.snackBarService.success('INTL_INVITATION_SENT_SUCCESSFULLY');
          this.close('invitation_sent');
        },
        error: (error) => {
          console.error('Error inviting GP user:', error);
          if (error?.status === 403 && shouldShowDdOptionUpsell(getSubscriptionDenialReason(error?.error))) {
            this.matDialog.open(SubscriptionUpgradeDialogComponent, {
              width: '900px',
              maxWidth: '95vw',
              disableClose: true,
              data: { organizationType: 'gp' },
            });
            return;
          }
          this.formErrorService.onServerError(error, this.form);
        },
      });
  }

  public create(): void {
    if (this.isPreparingInviteAdminFund || this.isLoadingIssuerAdmin || this.isCreating) {
      return;
    }

    if (!this.form.valid || !this.isFormReadyForCreate()) {
      return;
    }

    void this.withCreating(async () => {
      const existingProductUid = this.form.get('productUid')?.value as string | null;
      const existingIssuerUid = (this.form.get('issuerUid')?.value as string | null) || '';

      // Existing fund selected from search — navigate without creating again.
      if (existingProductUid) {
        this.snackBarService.success('INTL_FUND_CREATED_SUCCESSFULLY');
        await this.navigateToProductDataroom(existingProductUid, existingIssuerUid);
        return;
      }

      const request: CreateFundDDQRunRequest = this.buildCreateFundRequestFromForm();
      const response = await firstValueFrom(this.fundDDQRunService.createFundDDQRun(request).pipe(take(1)));

      if (!response) {
        return;
      }

      if (response.productUid) {
        const defaultTemplatesInitialized = await firstValueFrom(
          this.initializeEmittentDefaultTemplates(response.productUid, response.issuerUid).pipe(take(1)),
        );
        if (!defaultTemplatesInitialized) {
          return;
        }

        this.snackBarService.success('INTL_FUND_CREATED_SUCCESSFULLY');
        await this.navigateToProductDataroom(response.productUid, response.issuerUid, response);
      } else {
        this.snackBarService.success('INTL_FUND_CREATED_SUCCESSFULLY');
        this.close(response);
      }
    }).catch((error) => {
      this.formErrorService.onServerError(error, this.form);
    });
  }

  private async withCreating<T>(fn: () => Promise<T>): Promise<T> {
    this.isCreating = true;
    this.cdr.markForCheck();
    try {
      return await fn();
    } finally {
      this.isCreating = false;
      this.cdr.markForCheck();
    }
  }

  private initializeEmittentDefaultTemplates(productUid: string | null, emittentUid: string | null): Observable<boolean> {
    const clientScopeUid = extractClientContextFromUrl(this.router.url || '')?.clientId ?? null;
    const createdByUid = String(this.appStoreQuery.getValue()?.user?.id ?? localStorage.getItem('uid') ?? '').trim();
    let partnerUid = '';

    try {
      partnerUid = String(JSON.parse(localStorage.getItem('keys') || '{}')?.partneruid ?? '').trim();
    } catch {
      partnerUid = '';
    }

    if (!partnerUid || !clientScopeUid || !emittentUid || !productUid || !createdByUid) {
      console.warn('Default-template initialization skipped because its required context is unavailable.');
      return of(true);
    }

    const payload: InitializeEmittentDefaultTemplatesPayload = {
      partner_uid: partnerUid,
      client_scope_uid: clientScopeUid,
      emittent_uid: emittentUid,
      product_uid: productUid,
      created_by_uid: createdByUid,
    };

    return this.fundDDQRunService.initializeEmittentDefaultTemplates(payload).pipe(
      map(() => true),
      catchError((error) => {
        console.error('Failed to initialize emittent default templates:', error);
        this.snackBarService.open(
          'The fund or invitation was completed, but default-template setup failed. Please try again.',
          'warn',
        );
        return of(false);
      }),
    );
  }

  /**
   * After Create Fund / Direct Deal and Invite later, land on the product dataroom
   * (asset class / subclass / Keep Private are already set by the create API).
   */
  private async navigateToProductDataroom(
    productUid: string,
    issuerUid: string,
    closeResult?: FundDDQRun,
  ): Promise<void> {
    const navigatedToClientIssuing = await this.navigateToClientIssuingDataroomIfPossible(productUid, closeResult);
    if (navigatedToClientIssuing) {
      return;
    }

    this.organizationContextService.setContext({
      context: 'fund-ddq-runs',
      viewAs: 'investor',
      serviceType: 'emittent',
      issuerUid,
    });

    this.close(closeResult);

    const clientContext = extractClientContextFromUrl(this.router.url || '');
    const inAppRoute = buildFundDdqDataroomRoute(clientContext, productUid);
    const navigationExtras = fundDdqNavigationExtras(clientContext, issuerUid);

    if (inAppRoute) {
      await this.router.navigate(inAppRoute, navigationExtras);
      return;
    }

    await this.router.navigate([productRoute, productUid, dataroomRoute], navigationExtras);
  }

  private async navigateToClientIssuingDataroomIfPossible(
    productUid: string,
    closeResult?: FundDDQRun,
  ): Promise<boolean> {
    if (!this.clientService || !productUid) {
      return false;
    }

    const clientContext = extractClientContextFromUrl(this.router.url || '');
    // Global Fund DDQ Runs must go to /product/.../data-room — do not probe
    // accounts/client/.../client-issuings just because the user profile has a client uid.
    if (!clientContext) {
      return false;
    }

    const loggedInClientUid = this.getLoggedInClientUid();

    if (clientContext.isClientUserContext) {
      const clientId = loggedInClientUid;
      if (!clientId) {
        return false;
      }

      const clientIssuingId = await firstValueFrom(
        this.clientService.resolveClientIssuingId(clientId, productUid, true).pipe(take(1)),
      );
      if (!clientIssuingId) {
        // Newly created funds often have no client-issuing yet (or API returns 403).
        // Fall back to the global product dataroom route.
        return false;
      }

      this.close(closeResult);
      await this.router.navigate([
        '/client',
        clientId,
        'client-issuings',
        clientIssuingId,
        'issuing-process',
        clientIssuingId,
        dataroomRoute,
      ]);
      return true;
    }

    // LP advisor on /clients/{id}/dashboard/...
    const clientIssuingId = await firstValueFrom(
      this.clientService.resolveClientIssuingId(clientContext.clientId, productUid, false).pipe(take(1)),
    );
    if (!clientIssuingId) {
      return false;
    }

    this.close(closeResult);
    await this.router.navigate([
      '/clients',
      clientContext.clientId,
      'dashboard',
      'client-issuings',
      clientIssuingId,
      'issuing-process',
      clientIssuingId,
      dataroomRoute,
    ]);
    return true;
  }

  close(result?: FundDDQRun | 'invitation_sent'): void {
    // Reset form
    this.form.reset();

    // Reset component state
    this.issuerAdminResult = null;
    this.issuerAdminsList = [];
    this.issuerAdminsListSubject.next([]);
    this.issuerAdminEmailChoiceOptions = [
      new FormOption(this.issuerAdminEmailDropdownPlaceholderLabel, ''),
      new FormOption('Invite New Admin', this.inviteNewAdminChoiceValue),
    ];
    this.isLoadingIssuerAdmin = false;
    this.showFundTypeFields = false;
    this.isNewInvestor = false;
    this.formService.setNewInvestorFieldsRequired(false);
    this.showInvestorInviteWarning = false;
    this.hasSearchedIssuerAdmin = false;

    // Reset observables
    this.issuerOptions$ = of([]);
    this.fundOptions$ = of([]);

    if (result === 'invitation_sent') {
      this.ddqInvitationNavService.refreshHasInvitees().pipe(take(1)).subscribe();
    }

    // Close dialog with optional result (used by list to refresh)
    this.dialog.close(result);
  }
}
